import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  API_BASE_URL, NAVBAR_TABS, NAVBAR_COLUMNS, NAVBAR_MESSAGES, NAVBAR_BRAND_FIELDS,
  getSubmitButtonLabel, getContentUpdateDescription, getNavbarSheetUrl,
  MONTHS, NAVBAR_DUPLICATE_CHECK, findDuplicates,
} from "./constants/adminConstants";
import AdminLogoEditor from "./AdminLogoEditor";
import { resolveIcon } from "../Main/Footer/FooterData";
import { compressImageFile, validateImageFile } from "./compressImage";
import { uploadToCloudinary } from "./uploadImage";
import "./css/adminStyles.css";

const RELIGION_OPTIONS = ["Hindu", "Muslim", "Christian", "Sikh", "Buddhist", "Jewish", "Jain", "National", "LongWeekend"];

const ACTION_TYPE_OPTIONS = ["link", "scrollToSection", "openCalendar", "contactScroll"];

const QUICK_ACTION_TYPE_OPTIONS = ["whatsapp", "profile", "calendar", "schedule", "contact"];

const ICON_PRESETS = ["whatsapp", "profile", "calendar", "phone", "contact"];

const DEFAULT_RELIGION_COLORS = {
  Hindu: "#f97316", Muslim: "#00897b", Christian: "#1565c0", Sikh: "#c49000",
  Buddhist: "#6a1b9a", Jewish: "#2e7d32", Jain: "#c62828", National: "#546e7a", LongWeekend: "#4a5ae8", Global: "#ddd831",
};

const HOLIDAY_COLUMNS = [
  { key: "month", label: "Month" },
  { key: "day", label: "Day" },
  { key: "name", label: "Name" },
  { key: "type", label: "Type" },
  { key: "desc", label: "Description" },
];

const NOTE_COLUMNS = [
  { key: "month", label: "Month" },
  { key: "day", label: "Day" },
  { key: "days", label: "Days Array" },
  { key: "note", label: "Note" },
];

function TableWithScrollButtons({ children }) {
  const scrollRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (el) {
      setShowLeft(el.scrollLeft > 5);
      setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 5);
    }
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
      ro.disconnect();
    };
  }, [checkScroll]);

  const scrollTo = (dir) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: dir === "left" ? -300 : 300, behavior: "smooth" });
  };

  return (
    <div className="AdminTableScrollWrap">
      <div className="AdminScrollBar">
          <button className="AdminScrollBtn" onClick={() => scrollTo("left")} disabled={!showLeft} title="Scroll left">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="10 4 6 8 10 12" />
            </svg>
          </button>
          <button className="AdminScrollBtn" onClick={() => scrollTo("right")} disabled={!showRight} title="Scroll right">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 4 10 8 6 12" />
            </svg>
          </button>
        </div>
      <div ref={scrollRef} className="AdminTableScroll">
        {children}
      </div>
    </div>
  );
}

function AdminNavbarEditor({ user, section, onBack }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [activeTab, setActiveTab] = useState("toolLinks");
  const [sectionInfo, setSectionInfo] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [successTitle, setSuccessTitle] = useState("");
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState([]);
  const [duplicateActionLoading, setDuplicateActionLoading] = useState(false);
  const [arraySearch, setArraySearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);
  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [searchOpen]);

  const [imageSearch, setImageSearch] = useState("");
  const [imageSearchOpen, setImageSearchOpen] = useState(false);
  const imageSearchRef = useRef(null);
  useEffect(() => {
    if (imageSearchOpen && imageSearchRef.current) {
      imageSearchRef.current.focus();
    }
  }, [imageSearchOpen]);

  const [holidaySearch, setHolidaySearch] = useState("");
  const [holidaySearchOpen, setHolidaySearchOpen] = useState(false);
  const holidaySearchRef = useRef(null);
  useEffect(() => {
    if (holidaySearchOpen && holidaySearchRef.current) {
      holidaySearchRef.current.focus();
    }
  }, [holidaySearchOpen]);

  const [religionSearch, setReligionSearch] = useState("");
  const [religionSearchOpen, setReligionSearchOpen] = useState(false);
  const religionSearchRef = useRef(null);
  useEffect(() => {
    if (religionSearchOpen && religionSearchRef.current) {
      religionSearchRef.current.focus();
    }
  }, [religionSearchOpen]);

  const [noteSearch, setNoteSearch] = useState("");
  const [noteSearchOpen, setNoteSearchOpen] = useState(false);
  const noteSearchRef = useRef(null);
  useEffect(() => {
    if (noteSearchOpen && noteSearchRef.current) {
      noteSearchRef.current.focus();
    }
  }, [noteSearchOpen]);

  useEffect(() => {
    setArraySearch("");
    setSearchOpen(false);
    setImageSearch("");
    setImageSearchOpen(false);
    setHolidaySearch("");
    setHolidaySearchOpen(false);
    setReligionSearch("");
    setReligionSearchOpen(false);
    setNoteSearch("");
    setNoteSearchOpen(false);
  }, [activeTab]);
  const [waCountryCode, setWaCountryCode] = useState("91");
  const [waLocalNumber, setWaLocalNumber] = useState("");
  const waCountryCodeRef = useRef("91");
  const waLocalNumberRef = useRef("");
  const snapshotRef = useRef(null);
  const dragRef = useRef(null);
  const tabBarRef = useRef(null);

  useEffect(() => {
    const el = tabBarRef.current;
    if (!el) return;
    const wheelHandler = (e) => {
      if (e.deltaY !== 0) { e.preventDefault(); el.scrollLeft += e.deltaY; }
    };
    el.addEventListener("wheel", wheelHandler, { passive: false });
    return () => el.removeEventListener("wheel", wheelHandler);
  }, []);
  const tabDragRef = useRef(null);

  const normalizeContent = (raw) => {
    const merged = { ...raw };
    if (!merged.toolLinks) merged.toolLinks = [];
    const hasCalendar = merged.toolLinks.some((l) => l.label === "Holiday Calendar");
    if (!hasCalendar) {
      merged.toolLinks.push({ label: "Holiday Calendar", href: null, desc: "View holiday calendar", actionType: "openCalendar", active: true, order: merged.toolLinks.length });
    }
    if (!merged.toolImages) merged.toolImages = {};
    for (const key of Object.keys(merged.toolImages)) {
      const v = merged.toolImages[key];
      if (typeof v === "string") {
        merged.toolImages[key] = { png: v, gif: "" };
      }
    }
    if (!merged.toolImages["Holiday Calendar"]) {
      merged.toolImages["Holiday Calendar"] = { png: "", gif: "" };
    }
    if (!merged.brand) {
      merged.brand = { logoImage: "", logoGif: "", logoMainText: "TheVisaCo", logoSubText: "Zero Worry Visa" };
    }
    if (merged.calendar) {
      merged.calendar = {
        ...merged.calendar,
        religionColors: { ...DEFAULT_RELIGION_COLORS, ...(merged.calendar.religionColors || {}) },
      };
      delete merged.calendar.religionColorList;
    }
    return merged;
  };

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    const token = sessionStorage.getItem("tvc_token");
    try {
      const res = await fetch(`${API_BASE_URL}/content/${section.key}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data && data.section) {
        const merged = normalizeContent(data.section.content);
        setContent(merged);
        snapshotRef.current = JSON.stringify(merged);
        setSectionInfo({ version: data.section.version, updatedAt: data.section.updatedAt });
        const waNum = merged.whatsapp?.number || "";
        if (waNum) {
          let cc, local;
          if (waNum.startsWith("91")) { cc = "91"; local = waNum.slice(2); }
          else if (waNum.startsWith("1")) { cc = "1"; local = waNum.slice(1); }
          else { cc = waNum.slice(0, waNum.length > 10 ? 3 : 2); local = waNum.slice(waNum.length > 10 ? 3 : 2); }
          setWaCountryCode(cc);
          setWaLocalNumber(local);
          waCountryCodeRef.current = cc;
          waLocalNumberRef.current = local;
        }
      } else {
        setMessage(NAVBAR_MESSAGES.apiError);
      }
    } catch (err) {
      setMessage(NAVBAR_MESSAGES.connectionError);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSheet = () => {
    window.open(getNavbarSheetUrl(), "_blank", "noopener,noreferrer");
  };

  const handlePushToSheet = async () => {
    const token = sessionStorage.getItem("tvc_token");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/content/navbar/push-sheet`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content }),
      });
      if (!res.ok) {
        const text = await res.text();
        setMessage(`Push failed (${res.status}): ${text.substring(0, 200)}`);
        return;
      }
      const data = await res.json();
      setMessage(data.success ? "Pushed to Google Sheet successfully!" : data.error || "Push failed.");
    } catch (err) {
      setMessage("Push error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePullFromSheet = async () => {
    const token = sessionStorage.getItem("tvc_token");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/content/navbar/pull-sheet`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        const merged = normalizeContent(data.content);
        setContent(merged);
        snapshotRef.current = JSON.stringify(merged);
        setMessage("Pulled from Google Sheet. Review changes and click Publish to go live.");
      } else {
        setMessage(data.error || "Pull failed.");
      }
    } catch (err) {
      setMessage("Pull error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleArrayChange = (field, index, key, value) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      const oldLabel = arr[index]?.label;
      arr[index] = { ...arr[index], [key]: value };
      updated[field] = arr;
      if (key === "label" && value) {
        if (field === "toolLinks") {
          const images = { ...(updated.toolImages || {}) };
          if (oldLabel && oldLabel !== value && images[oldLabel]) {
            images[value] = images[oldLabel];
            delete images[oldLabel];
          } else if (!images[value]) {
            images[value] = { png: "", gif: "" };
          }
          updated.toolImages = images;
          if (oldLabel && oldLabel !== value) {
            const renameKey = (obj, suffix) => {
              if (!obj) return obj;
              const oldK = `${oldLabel}-${suffix}`;
              const newK = `${value}-${suffix}`;
              if (obj[oldK]) {
                const o = { ...obj };
                o[newK] = o[oldK];
                delete o[oldK];
                return o;
              }
              return obj;
            };
            updated.imageHistory = renameKey(updated.imageHistory, "png");
            updated.imageHistory = renameKey(updated.imageHistory, "gif");
            updated.imageBin = renameKey(updated.imageBin, "png");
            updated.imageBin = renameKey(updated.imageBin, "gif");
          }
        } else if (field === "tabs") {
          if (!arr[index].png && !arr[index].gif) {
            arr[index] = { ...arr[index], png: "", gif: "" };
          }
        }
      }
      return updated;
    });
  };

  const handleIconPresetSelect = (field, index, key, kindKey, preset) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      arr[index] = { ...arr[index], [key]: preset, [kindKey]: preset };
      updated[field] = arr;
      return updated;
    });
  };

  const handleAddRowBefore = (field, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      const emptyRow = {};
      for (const key of Object.keys(arr[0] || {})) {
        emptyRow[key] = "";
      }
      arr.splice(index, 0, emptyRow);
      updated[field] = arr;
      return updated;
    });
  };

  const handleAddRowAfter = (field, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      const emptyRow = {};
      for (const key of Object.keys(arr[0] || {})) {
        emptyRow[key] = "";
      }
      arr.splice(index + 1, 0, emptyRow);
      updated[field] = arr;
      return updated;
    });
  };

  const handleDeleteRow = (field, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = (updated[field] || []);
      const deletedItem = arr[index];
      updated[field] = arr.filter((_, i) => i !== index);
      if (field === "toolLinks" && deletedItem?.label && updated.toolImages?.[deletedItem.label]) {
        const images = { ...updated.toolImages };
        delete images[deletedItem.label];
        updated.toolImages = images;
      }
      return updated;
    });
  };

  const handleArrayIconUpload = (field, index, key) => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "image/png";
    fileInput.style.display = "none";
    fileInput.addEventListener("change", () => {
      const file = fileInput.files?.[0];
      if (!file) return;
      const check = validateImageFile(file);
      if (!check.valid) { alert(check.message); return; }
      compressImageFile(file, 48, 0.8).then(async (dataUrl) => {
        try {
          const url = await uploadToCloudinary(dataUrl, file.name);
          handleArrayChange(field, index, key, url);
        } catch (err) {
          alert("Upload failed: " + err.message);
        }
      });
    });
    document.body.appendChild(fileInput);
    fileInput.click();
    setTimeout(() => {
      if (fileInput.parentNode) document.body.removeChild(fileInput);
    }, 60000);
  };

  const handleMoveRowUp = (field, index) => {
    if (index === 0) return;
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      updated[field] = arr;
      return updated;
    });
  };

  const handleMoveRowDown = (field, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      if (index >= arr.length - 1) return prev;
      [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
      updated[field] = arr;
      return updated;
    });
  };

  const handleFieldChange = (field, value) => {
    setContent((prev) => {
      if (field.startsWith("brand.")) {
        const parts = field.split(".");
        return { ...prev, brand: { ...prev.brand, [parts[1]]: value } };
      }
      if (field.startsWith("whatsapp.")) {
        const parts = field.split(".");
        return { ...prev, whatsapp: { ...prev.whatsapp, [parts[1]]: value } };
      }
      if (field.startsWith("calendar.")) {
        const parts = field.split(".");
        return { ...prev, calendar: { ...prev.calendar, [parts[1]]: value } };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleCalendarArrayChange = (subField, index, key, value) => {
    setContent((prev) => {
      const calendar = { ...(prev.calendar || {}) };
      let arr = subField === "religionColorList" ? [...initReligionColorList(calendar)] : [...(calendar[subField] || [])];
      if (key === "day") {
        arr[index] = { ...arr[index], [key]: parseInt(value, 10) || 0 };
      } else if (key === "days" && typeof value === "string") {
        arr[index] = { ...arr[index], [key]: value };
      } else if (key === "lw" || key === "active") {
        arr[index] = { ...arr[index], [key]: value === true || value === "true" };
      } else {
        arr[index] = { ...arr[index], [key]: value };
      }
      calendar[subField] = arr;
      return { ...prev, calendar };
    });
  };

  const initReligionColorList = (calendar) => {
    if (!calendar.religionColorList || calendar.religionColorList.length === 0) {
      const source = calendar.religionColors || DEFAULT_RELIGION_COLORS;
      return Object.entries(source).map(([k, v]) => ({ key: k, value: v }));
    }
    return calendar.religionColorList;
  };

  const handleAddCalendarRowBefore = (subField, index) => {
    setContent((prev) => {
      const calendar = { ...(prev.calendar || {}) };
      const arr = subField === "religionColorList" ? [...initReligionColorList(calendar)] : [...(calendar[subField] || [])];
      const emptyRow = {};
      for (const key of Object.keys(arr[0] || {})) {
        emptyRow[key] = Array.isArray(arr[0][key]) ? [] : "";
      }
      arr.splice(index, 0, emptyRow);
      calendar[subField] = arr;
      return { ...prev, calendar };
    });
  };

  const handleAddCalendarRowAfter = (subField, index) => {
    setContent((prev) => {
      const calendar = { ...(prev.calendar || {}) };
      const arr = subField === "religionColorList" ? [...initReligionColorList(calendar)] : [...(calendar[subField] || [])];
      const emptyRow = {};
      for (const key of Object.keys(arr[0] || {})) {
        emptyRow[key] = Array.isArray(arr[0][key]) ? [] : "";
      }
      arr.splice(index + 1, 0, emptyRow);
      calendar[subField] = arr;
      return { ...prev, calendar };
    });
  };

  const handleDeleteCalendarRow = (subField, index) => {
    setContent((prev) => {
      const calendar = { ...(prev.calendar || {}) };
      const arr = subField === "religionColorList" ? initReligionColorList(calendar) : (calendar[subField] || []);
      calendar[subField] = arr.filter((_, i) => i !== index);
      return { ...prev, calendar };
    });
  };

  const handleMoveCalendarRowUp = (subField, index) => {
    if (index === 0) return;
    setContent((prev) => {
      const calendar = { ...(prev.calendar || {}) };
      const arr = subField === "religionColorList" ? [...initReligionColorList(calendar)] : [...(calendar[subField] || [])];
      [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      calendar[subField] = arr;
      return { ...prev, calendar };
    });
  };

  const handleMoveCalendarRowDown = (subField, index) => {
    setContent((prev) => {
      const calendar = { ...(prev.calendar || {}) };
      const arr = subField === "religionColorList" ? [...initReligionColorList(calendar)] : [...(calendar[subField] || [])];
      if (index >= arr.length - 1) return prev;
      [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
      calendar[subField] = arr;
      return { ...prev, calendar };
    });
  };

  const handleSubmit = async () => {
    const dupes = findDuplicates(content, NAVBAR_DUPLICATE_CHECK);
    if (dupes.length > 0) {
      setDuplicateInfo(dupes);
      setShowDuplicateModal(true);
      return;
    }
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit();
  };

  const handleConfirmSubmit = async () => {
    if (!noteText.trim()) return;
    setShowNoteModal(false);
    await doSubmit(noteText.trim());
  };

  const handleContinueAnyway = async () => {
    setShowDuplicateModal(false);
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit();
  };

  const handleRemoveDuplicates = async () => {
    setDuplicateActionLoading(true);
    const cleanContent = JSON.parse(JSON.stringify(content));
    const sorted = [...duplicateInfo].sort((a, b) => b.duplicateIndex - a.duplicateIndex);
    for (const dup of sorted) {
      if (dup.parent) {
        if (!cleanContent[dup.parent]) continue;
        const arr = [...(cleanContent[dup.parent][dup.field] || [])];
        if (arr[dup.duplicateIndex]) {
          arr.splice(dup.duplicateIndex, 1);
          cleanContent[dup.parent] = { ...cleanContent[dup.parent], [dup.field]: arr };
        }
      } else {
        const arr = [...(cleanContent[dup.field] || [])];
        if (arr[dup.duplicateIndex]) {
          arr.splice(dup.duplicateIndex, 1);
          cleanContent[dup.field] = arr;
        }
      }
    }
    setContent(cleanContent);
    setShowDuplicateModal(false);
    setDuplicateActionLoading(false);
    snapshotRef.current = JSON.stringify(cleanContent);
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit("", cleanContent);
  };

  const doSubmit = async (submitterNote, overrideContent) => {
    setMessage("");
    setSaving(true);
    const token = sessionStorage.getItem("tvc_token");

    const payload = overrideContent || { ...content };
    if (payload.brand) {
      payload.brand = { ...payload.brand };
      delete payload.brand.logoHistory;
      delete payload.brand.logoBin;
      delete payload.brand.gifHistory;
      delete payload.brand.gifBin;
    }
    if (payload.calendar?.religionColorList) {
      const obj = {};
      for (const item of payload.calendar.religionColorList) {
        if (item.key) obj[item.key] = item.value || "#888";
      }
      payload.calendar = { ...payload.calendar, religionColors: obj };
    }
    if (payload.calendar?.longWeekendNotes) {
      payload.calendar.longWeekendNotes = payload.calendar.longWeekendNotes.map(note => {
        if (typeof note.days === "string") {
          return { ...note, days: note.days.split(",").filter(s => s.trim() !== "").map(Number) };
        }
        return note;
      });
    }

    const estimateSize = new Blob([JSON.stringify(payload)]).size;
    if (estimateSize > 5 * 1024 * 1024) {
      setMessage(`Payload too large (${(estimateSize / 1024 / 1024).toFixed(1)} MB). Reduce image sizes before submitting.`);
      setSaving(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/content/submit-change`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          description: getContentUpdateDescription(section.label),
          changes: {
            type: "content_update",
            sectionKey: section.key,
            content: payload,
            ...(submitterNote ? { submitterNote } : {}),
          },
          sectionKey: section.key,
        }),
      });

      let data;
      try { data = await res.json(); } catch { data = { error: `Server returned ${res.status}` }; }
      if (res.ok) {
        snapshotRef.current = JSON.stringify(content);
        const role = user?.role || "Staff";
        setSuccessTitle(role === "Admin" ? "Changes Published" : "Request made");
        setShowSuccessOverlay(true);
      } else {
        setMessage(data.error || NAVBAR_MESSAGES.submitFailed);
      }
    } catch (err) {
      setMessage(`Network error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const renderArrayEditor = (field, columns) => {
    const items = content?.[field] || [];
    return (
      <div className="AdminCardMb">
        <div className="AdminSectionHeader">
          <h4 className="AdminSectionTitle">
            {field.replace(/([A-Z])/g, " $1").trim()}
          </h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <div className={`AdminPillSearchWrap ${searchOpen ? 'is-open' : ''}`}>
              <span className="AdminPillSearchIcon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </span>
              <input
                ref={searchRef}
                type="text"
                className="AdminPillSearch"
                placeholder="Search..."
                value={arraySearch}
                onChange={(e) => setArraySearch(e.target.value)}
                onBlur={() => { setArraySearch(""); setTimeout(() => setSearchOpen(false), 200) }}
              />
              {!searchOpen && (
                <button className="AdminPillSearchOverlay" onClick={() => setSearchOpen(true)} type="button" aria-label="Search" />
              )}
            </div>
            <button
              className="AdminButton AdminButtonSmall AdminButtonSecondary AdminAddRowBtn"
              onClick={() => handleAddRowAfter(field, items.length - 1)}
            >
              + Add Row
            </button>
          </div>
        </div>
        <TableWithScrollButtons>
          <table className="AdminTable AdminTableEditor">
            <thead>
              <tr>
                <th className="AdminThOrder">Order</th>
                {columns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
                <th className="AdminThActions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items
                .map((item, idx) => ({ item, idx }))
                .filter(({ item }) => {
                  if (!arraySearch.trim()) return true;
                  return columns.some((col) => {
                    const val = col.type === "checkbox" ? (item[col.key] ? "true" : "false") : item[col.key];
                    return String(val || "").toLowerCase().includes(arraySearch.trim().toLowerCase());
                  });
                })
                .map(({ item, idx }) => (
                <tr
                  key={idx}
                  draggable="true"
                  onDragStart={(e) => {
                    dragRef.current = { type: "array", field, fromIndex: idx };
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", JSON.stringify(dragRef.current));
                    e.currentTarget.style.opacity = "0.4";
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    e.currentTarget.style.outline = "2px dashed #3b82f6";
                    e.currentTarget.style.outlineOffset = "-2px";
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.style.outline = "";
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.style.opacity = "";
                    e.currentTarget.style.outline = "";
                    if (!dragRef.current || dragRef.current.type !== "array") { dragRef.current = null; return; }
                    const { field: fromField, fromIndex } = dragRef.current;
                    if (fromField !== field || fromIndex === idx) { dragRef.current = null; return; }
                    setContent((prev) => {
                      const updated = { ...prev };
                      const arr = [...(updated[field] || [])];
                      const [moved] = arr.splice(fromIndex, 1);
                      arr.splice(idx, 0, moved);
                      updated[field] = arr;
                      return updated;
                    });
                    dragRef.current = null;
                  }}
                  onDragEnd={(e) => {
                    e.currentTarget.style.opacity = "";
                    e.currentTarget.style.outline = "";
                    dragRef.current = null;
                  }}
                >
                  <td>
                    <div className="AdminOrderBtns">
                      <button className="AdminMoveBtn" onClick={() => handleMoveRowUp(field, idx)} disabled={idx === 0} title="Move up">&#8593;</button>
                      <button className="AdminMoveBtn" onClick={() => handleMoveRowDown(field, idx)} disabled={idx === items.length - 1} title="Move down">&#8595;</button>
                    </div>
                  </td>
                  {columns.map((col) => (
                    <td key={col.key}>
                      {col.type === "icon" ? (
                        <div className="AdminIconCell">
                          <span className="AdminIconPreview">
                            {resolveIcon(item[col.key])}
                          </span>
                          <input
                            className="AdminInput AdminTableInput"
                            value={item[col.kindKey || col.key] || ""}
                            onChange={(e) => handleArrayChange(field, idx, col.kindKey || col.key, e.target.value)}
                            placeholder={col.label}
                          />
                          <select
                            className="AdminInput AdminTableInput AdminMinW90"
                            value=""
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v) handleIconPresetSelect(field, idx, col.key, col.kindKey, v);
                            }}
                          >
                            <option value="">Presets...</option>
                            {ICON_PRESETS.map((p) => (
                              <option key={p} value={p}>{p}</option>
                            ))}
                          </select>
                          <button className="AdminIconUploadBtn" title="Upload icon image" onClick={() => handleArrayIconUpload(field, idx, col.key)}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z"/>
                            </svg>
                          </button>
                        </div>
                      ) : col.type === "checkbox" ? (
                        <input
                          type="checkbox"
                          className="AdminCheckbox"
                          checked={item[col.key] === true || item[col.key] === "true"}
                          onChange={(e) => handleArrayChange(field, idx, col.key, e.target.checked)}
                        />
                      ) : col.key === "actionType" || col.key === "type" ? (
                        <select
                          className="AdminInput AdminTableInput"
                          value={item[col.key] || ""}
                          onChange={(e) => handleArrayChange(field, idx, col.key, e.target.value)}
                        >
                          <option value="">Select...</option>
                          {(col.key === "type" ? QUICK_ACTION_TYPE_OPTIONS : ACTION_TYPE_OPTIONS).map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          className="AdminInput AdminTableInput"
                          value={item[col.key] || ""}
                          onChange={(e) => handleArrayChange(field, idx, col.key, e.target.value)}
                          placeholder={col.label}
                        />
                      )}
                    </td>
                  ))}
                  <td>
                    <div className="AdminActionBtns">
                      <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => handleAddRowBefore(field, idx)} title="Add row before">&#9650;+</button>
                      <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => handleAddRowAfter(field, idx)} title="Add row after">&#9660;+</button>
                      <button className="AdminIconBtn AdminIconBtnRed" onClick={() => handleDeleteRow(field, idx)} title="Delete row">&#10005;</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWithScrollButtons>
        {items.length === 0 && (
          <div className="AdminEmptyState">{NAVBAR_MESSAGES.emptyArray}</div>
        )}
        {items.length > 0 && arraySearch.trim() && !items.some((item) =>
          columns.some((col) => {
            const val = col.type === "checkbox" ? (item[col.key] ? "true" : "false") : item[col.key];
            return String(val || "").toLowerCase().includes(arraySearch.trim().toLowerCase());
          })
        ) && (
          <div className="AdminEmptyState">No results found for "{arraySearch}"</div>
        )}
      </div>
    );
  };

  const [logoEditorOpen, setLogoEditorOpen] = React.useState(false);
  const [logoEditorImage, setLogoEditorImage] = React.useState(null);
  const [logoEditorCallback, setLogoEditorCallback] = React.useState(null);
  const [logoEditorRefKey, setLogoEditorRefKey] = React.useState(null);
  const [logoEditorAccept, setLogoEditorAccept] = React.useState("image/*");
  const [brandLogoEditorOpen, setBrandLogoEditorOpen] = React.useState(false);
  const [brandGifEditorOpen, setBrandGifEditorOpen] = React.useState(false);

  const handleIconUploadClick = (row, field) => {
    const isPngCol = field === "png";
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = isPngCol ? "image/png" : "image/gif";
    fileInput.style.display = "none";
    fileInput.addEventListener("change", () => {
      const file = fileInput.files?.[0];
      if (!file) return;
      if (isPngCol && file.type !== "image/png") { alert("Only PNG files are allowed in the PNG column."); return; }
      if (!isPngCol && file.type !== "image/gif") { alert("Only GIF files are allowed in the GIF column."); return; }
      const check = validateImageFile(file);
      if (!check.valid) { alert(check.message); return; }
      compressImageFile(file, 200, 0.85).then(async (dataUrl) => {
        try {
          const url = await uploadToCloudinary(dataUrl, file.name);
          const oldUrl = row[field];
          updateFieldRef.current(row, field, url);
          if (oldUrl) {
            const fieldKey = `${row.name}-${field}`;
            setContent(prev => {
              const history = { ...(prev.imageHistory || {}) };
              const existing = history[fieldKey] || [];
              history[fieldKey] = [...existing.filter(u => u !== oldUrl), oldUrl];
              return { ...prev, imageHistory: history };
            });
          }
        } catch (err) {
          alert("Upload failed: " + err.message);
        }
      });
    });
    document.body.appendChild(fileInput);
    fileInput.click();
    setTimeout(() => {
      if (fileInput.parentNode) document.body.removeChild(fileInput);
    }, 60000);
  };

  const updateFieldRef = React.useRef(null);

  const handleLogoEditorSave = async (newUrl) => {
    if (!logoEditorCallback) return;
    const oldUrl = logoEditorImage;
    let finalUrl = newUrl;
    if (newUrl && newUrl.startsWith("data:")) {
      try {
        finalUrl = await uploadToCloudinary(newUrl, "tool-icon");
      } catch (err) {
        alert("Upload failed: " + err.message);
        return;
      }
    }
    logoEditorCallback(finalUrl);
    if (logoEditorRefKey && oldUrl && oldUrl !== finalUrl) {
      setContent(prev => {
        const history = { ...(prev.imageHistory || {}) };
        const existing = history[logoEditorRefKey] || [];
        history[logoEditorRefKey] = [...existing.filter(u => u !== oldUrl), oldUrl];
        return { ...prev, imageHistory: history };
      });
    }
    setLogoEditorOpen(false);
    setLogoEditorImage(null);
    setLogoEditorCallback(null);
    setLogoEditorRefKey(null);
    setLogoEditorAccept("image/*");
  };

  const handleLogoEditorClose = () => {
    setLogoEditorOpen(false);
    setLogoEditorImage(null);
    setLogoEditorCallback(null);
    setLogoEditorRefKey(null);
    setLogoEditorAccept("image/*");
  };

  const handleLogoEditorDeleteHistory = (index) => {
    if (!logoEditorRefKey) return;
    setContent(prev => {
      const history = { ...(prev.imageHistory || {}) };
      const bin = { ...(prev.imageBin || {}) };
      const arr = history[logoEditorRefKey] || [];
      const removed = arr[index];
      if (!removed) return prev;
      history[logoEditorRefKey] = arr.filter((_, i) => i !== index);
      bin[logoEditorRefKey] = [...(bin[logoEditorRefKey] || []), removed];
      return { ...prev, imageHistory: history, imageBin: bin };
    });
  };

  const handleLogoEditorRestoreBin = (index) => {
    if (!logoEditorRefKey) return;
    setContent(prev => {
      const bin = { ...(prev.imageBin || {}) };
      const history = { ...(prev.imageHistory || {}) };
      const arr = bin[logoEditorRefKey] || [];
      const restored = arr[index];
      if (!restored) return prev;
      bin[logoEditorRefKey] = arr.filter((_, i) => i !== index);
      history[logoEditorRefKey] = [...(history[logoEditorRefKey] || []), restored];
      return { ...prev, imageBin: bin, imageHistory: history };
    });
  };

  const handleLogoEditorPermanentDelete = (index) => {
    if (!logoEditorRefKey) return;
    setContent(prev => {
      const bin = { ...(prev.imageBin || {}) };
      const arr = bin[logoEditorRefKey] || [];
      if (!arr[index]) return prev;
      bin[logoEditorRefKey] = arr.filter((_, i) => i !== index);
      return { ...prev, imageBin: bin };
    });
  };

  const normalizeImgObj = (v) => {
    if (!v) return { png: "", gif: "", pngName: "", gifName: "" };
    if (typeof v === "object") return { png: v.png || "", gif: v.gif || "", pngName: v.pngName || "", gifName: v.gifName || "" };
    return { png: v, gif: "", pngName: "", gifName: "" };
  };

  const renderToolImagesEditor = () => {
    const toolImgEntries = Object.entries(content?.toolImages || {});
    const tabItems = (content?.tabs || []);
    const allRows = [
      ...toolImgEntries
        .filter(([label]) => label !== "R" && label !== "Ra")
        .map(([label, img]) => ({ name: label, section: "Tool", ...normalizeImgObj(img), isTool: true, tabIdx: -1 })),
      ...tabItems
        .filter((t) => (t.label || t.id) !== "sham")
        .map((t, i) => {
        const imgs = normalizeImgObj(t.png || t.gif ? t : "");
        return { name: t.label || t.id, section: "Tab", ...imgs, tabIdx: i };
      }),
    ];

    const updateField = (row, field, value) => {
      if (row.isTool) {
        setContent((prev) => {
          const cur = normalizeImgObj(prev.toolImages?.[row.name]);
          return {
            ...prev,
            toolImages: { ...prev.toolImages, [row.name]: { ...cur, [field]: value } },
          };
        });
      } else {
        setContent((prev) => {
          const newTabs = [...(prev.tabs || [])];
          newTabs[row.tabIdx] = { ...newTabs[row.tabIdx], [field]: value };
          return { ...prev, tabs: newTabs };
        });
      }
    };
    updateFieldRef.current = updateField;

    const renderField = (row, field) => {
      const url = row[field];
      const name = row[field + "Name"] || "";
      const isPng = field === "png";
      const fieldKey = `${row.name}-${field}`;
      return (
        <div className="AdminFlexNoWrap">
          {url && (
            <img
              src={url}
              alt={row.name}
              className="AdminToolImg"
              onError={(e) => { e.target.style.display = "none" }}
            />
          )}
          <input
            className="AdminInput AdminTableInput AdminMinW140"
            value={name}
            onChange={(e) => updateField(row, field + "Name", e.target.value)}
            placeholder={`Name for ${isPng ? "PNG" : "GIF"} image`}
          />
          <button
            className="AdminIconUploadBtn"
            title="Upload"
            onClick={() => handleIconUploadClick(row, field)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z"/>
            </svg>
          </button>
          {url && (
            <>
              <button
                className="AdminIconUploadBtn"
                title="Edit"
                onClick={() => {
                  setLogoEditorImage(url);
                  setLogoEditorRefKey(fieldKey);
                  setLogoEditorAccept(isPng ? "image/png" : "image/gif");
                  setLogoEditorCallback(() => (newUrl) => updateField(row, field, newUrl));
                  setLogoEditorOpen(true);
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              </button>
              <button
                className="AdminIconBtn AdminIconBtnRed"
                title="Clear"
                onClick={() => {
                  const oldUrl = row[field];
                  if (oldUrl) {
                    const fieldKey = `${row.name}-${field}`;
                    setContent(prev => {
                      const history = { ...(prev.imageHistory || {}) };
                      const existing = history[fieldKey] || [];
                      history[fieldKey] = [...existing.filter(u => u !== oldUrl), oldUrl];
                      return { ...prev, imageHistory: history };
                    });
                  }
                  updateField(row, field, "");
                  updateField(row, field + "Name", "");
                }}
              >
                &#10005;
              </button>
            </>
          )}
        </div>
      );
    };

    const filteredRows = allRows.filter((row) => {
      if (!imageSearch.trim()) return true;
      const q = imageSearch.trim().toLowerCase();
      return row.name.toLowerCase().includes(q) || row.section.toLowerCase().includes(q);
    });

    return (
      <div className="AdminCardMb">
        <div className="AdminSectionHeader">
          <h4 className="AdminSectionTitle">Image</h4>
          <span className="AdminText12 AdminTextMuted">PNG &amp; GIF URLs for tool links and hero tabs.</span>
          <div className={`AdminPillSearchWrap ${imageSearchOpen ? 'is-open' : ''}`}>
            <span className="AdminPillSearchIcon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </span>
            <input
              ref={imageSearchRef}
              type="text"
              className="AdminPillSearch"
              placeholder="Search..."
              value={imageSearch}
              onChange={(e) => setImageSearch(e.target.value)}
               onBlur={() => { setImageSearch(""); setTimeout(() => setImageSearchOpen(false), 200) }}
            />
            {!imageSearchOpen && (
              <button className="AdminPillSearchOverlay" onClick={() => setImageSearchOpen(true)} type="button" aria-label="Search" />
            )}
          </div>
        </div>
        <TableWithScrollButtons>
          <table className="AdminTable AdminTableEditor">
            <thead>
              <tr>
                <th>Item</th>
                <th>Section</th>
                <th>PNG</th>
                <th>GIF</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row, ri) => (
                <tr key={ri}>
                  <td className="AdminNowrap"><strong>{row.name}</strong></td>
                  <td className="AdminNowrap"><span className={`AdminText12 ${row.section === "Tool" ? "AdminTextOrangeTool" : "AdminTextBlueTab"}`}>{row.section}</span></td>
                  <td className="AdminNowrap">{renderField(row, "png")}</td>
                  <td className="AdminNowrap">{renderField(row, "gif")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWithScrollButtons>
        {imageSearch.trim() && filteredRows.length === 0 && (
          <div className="AdminEmptyState">No images found for "{imageSearch}"</div>
        )}
      </div>
    );
  };

  const brandLogoHistory = content?.brand?.logoHistory || [];
  const brandLogoBin = content?.brand?.logoBin || [];
  const brandGifHistory = content?.brand?.gifHistory || [];
  const brandGifBin = content?.brand?.gifBin || [];

  const handleBrandLogoEditorSave = async (dataUrl) => {
    let finalUrl = dataUrl;
    if (dataUrl && dataUrl.startsWith("data:")) {
      try {
        finalUrl = await uploadToCloudinary(dataUrl, "brand-logo");
      } catch (err) {
        alert("Upload failed: " + err.message);
        return;
      }
    }
    setContent((prev) => {
      const currentLogo = prev?.brand?.logoImage;
      const history = prev?.brand?.logoHistory || [];
      const newHistory = currentLogo && currentLogo !== finalUrl
        ? [currentLogo, ...history.filter((h) => h !== currentLogo && h !== finalUrl)].slice(0, 20)
        : history;
      return { ...prev, brand: { ...prev?.brand, logoImage: finalUrl, logoHistory: newHistory } };
    });
    setBrandLogoEditorOpen(false);
  };

  const handleBrandGifEditorSave = async (dataUrl) => {
    let finalUrl = dataUrl;
    if (dataUrl && dataUrl.startsWith("data:")) {
      try {
        finalUrl = await uploadToCloudinary(dataUrl, "brand-gif");
      } catch (err) {
        alert("Upload failed: " + err.message);
        return;
      }
    }
    setContent((prev) => {
      const currentGif = prev?.brand?.logoGif;
      const history = prev?.brand?.gifHistory || [];
      const newHistory = currentGif && currentGif !== finalUrl
        ? [currentGif, ...history.filter((h) => h !== currentGif && h !== finalUrl)].slice(0, 20)
        : history;
      return { ...prev, brand: { ...prev?.brand, logoGif: finalUrl, gifHistory: newHistory } };
    });
    setBrandGifEditorOpen(false);
  };

  const handleBrandLogoDeleteHistory = (index) => {
    setContent((prev) => {
      const history = [...(prev?.brand?.logoHistory || [])];
      const bin = [...(prev?.brand?.logoBin || [])];
      const deleted = history.splice(index, 1);
      if (deleted.length) bin.push(deleted[0]);
      return { ...prev, brand: { ...prev?.brand, logoHistory: history, logoBin: bin } };
    });
  };

  const handleBrandGifDeleteHistory = (index) => {
    setContent((prev) => {
      const history = [...(prev?.brand?.gifHistory || [])];
      const bin = [...(prev?.brand?.gifBin || [])];
      const deleted = history.splice(index, 1);
      if (deleted.length) bin.push(deleted[0]);
      return { ...prev, brand: { ...prev?.brand, gifHistory: history, gifBin: bin } };
    });
  };

  const handleBrandLogoRestoreBin = (index) => {
    setContent((prev) => {
      const bin = [...(prev?.brand?.logoBin || [])];
      const history = [...(prev?.brand?.logoHistory || [])];
      const restored = bin.splice(index, 1);
      if (restored.length) history.unshift(restored[0]);
      return { ...prev, brand: { ...prev?.brand, logoHistory: history, logoBin: bin } };
    });
  };

  const handleBrandGifRestoreBin = (index) => {
    setContent((prev) => {
      const bin = [...(prev?.brand?.gifBin || [])];
      const history = [...(prev?.brand?.gifHistory || [])];
      const restored = bin.splice(index, 1);
      if (restored.length) history.unshift(restored[0]);
      return { ...prev, brand: { ...prev?.brand, gifHistory: history, gifBin: bin } };
    });
  };

  const renderBrandEditor = () => {
    const brand = content?.brand || {};
    return (
      <div className="AdminCardMb">
        <div className="AdminSectionHeader">
          <h4 className="AdminSectionTitle">Brand Information</h4>
        </div>
        <div className="AdminBrandGrid">
          <div className="AdminFormGroup">
            <label className="AdminLabel">Logo Image</label>
            {brand.logoImage ? (
              <div className="AdminLogoPreview" onClick={() => setBrandLogoEditorOpen(true)}>
                <img src={brand.logoImage} alt="Navbar Logo" className="AdminLogoPreviewImg" />
              </div>
            ) : (
              <label className="AdminLogoPreview">
                <div className="AdminLogoPreviewPlaceholder">Click to upload logo</div>
                <input type="file" accept="image/png" className="AdminHidden" onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.type !== "image/png") { alert("Only PNG files are allowed."); return; }
                  const check = validateImageFile(file);
                  if (!check.valid) { alert(check.message); return; }
                  compressImageFile(file, 200, 0.85).then(async (dataUrl) => {
                    try {
                      const url = await uploadToCloudinary(dataUrl, file.name);
                      handleFieldChange("brand.logoImage", url);
                    } catch (err) {
                      alert("Upload failed: " + err.message);
                    }
                  });
                  e.target.value = "";
                }} />
              </label>
            )}
          </div>
          <div className="AdminFormGroup">
            <label className="AdminLabel">Logo GIF</label>
            {brand.logoGif ? (
              <div className="AdminLogoPreview" onClick={() => setBrandGifEditorOpen(true)}>
                <img src={brand.logoGif} alt="Navbar GIF" className="AdminLogoPreviewImg" />
              </div>
            ) : (
              <label className="AdminLogoPreview">
                <div className="AdminLogoPreviewPlaceholder">Click to upload GIF</div>
                <input type="file" accept="image/gif" className="AdminHidden" onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.type !== "image/gif") { alert("Only GIF files are allowed."); return; }
                  const check = validateImageFile(file);
                  if (!check.valid) { alert(check.message); return; }
                  const reader = new FileReader();
                  reader.onload = async () => {
                    try {
                      const url = await uploadToCloudinary(reader.result, file.name);
                      handleFieldChange("brand.logoGif", url);
                    } catch (err) {
                      alert("Upload failed: " + err.message);
                    }
                  };
                  reader.readAsDataURL(file);
                  e.target.value = "";
                }} />
              </label>
            )}
          </div>
          <div className="AdminFormGroup">
            <label className="AdminLabel">Logo Main Text</label>
            <input
              className="AdminInput"
              value={brand.logoMainText || ""}
              onChange={(e) => handleFieldChange("brand.logoMainText", e.target.value)}
              placeholder="TheVisaCo"
            />
          </div>
          <div className="AdminFormGroup">
            <label className="AdminLabel">Logo Sub Text</label>
            <input
              className="AdminInput"
              value={brand.logoSubText || ""}
              onChange={(e) => handleFieldChange("brand.logoSubText", e.target.value)}
              placeholder="Zero Worry Visa"
            />
          </div>
        </div>
      </div>
    );
  };

  const renderWhatsAppEditor = () => {
    const wa = content?.whatsapp || {};
    return (
      <div className="AdminCardMb">
        <div className="AdminSectionHeader">
          <h4 className="AdminSectionTitle">WhatsApp Configuration</h4>
        </div>

        <div className="AdminGrid2Gap20">
          <div className="AdminFlexRowGap12">
            <div className="AdminFormGroup AdminFlex0140">
              <label className="AdminLabel">Country Code</label>
              <input
                className="AdminInput"
                value={waCountryCode ? `+${waCountryCode}` : ""}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^\d+]/g, "").replace(/\+/g, "");
                  const v = raw.slice(0, 4);
                  setWaCountryCode(v);
                  waCountryCodeRef.current = v;
                  setContent((prev) => {
                    const wa2 = prev.whatsapp || {};
                    return { ...prev, whatsapp: { ...wa2, number: v + (waLocalNumberRef.current || "") } };
                  });
                }}
                placeholder="+91"
              />
            </div>
            <div className="AdminFormGroup AdminFlex1">
              <label className="AdminLabel">Phone Number</label>
              <input
                className="AdminInput"
                value={waLocalNumber}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 10);
                  setWaLocalNumber(v);
                  waLocalNumberRef.current = v;
                  setContent((prev) => {
                    const wa2 = prev.whatsapp || {};
                    return { ...prev, whatsapp: { ...wa2, number: (waCountryCodeRef.current || "91") + v } };
                  });
                }}
                placeholder="9810044015"
              />
              {waLocalNumber.length > 0 && waLocalNumber.length !== 10 && (
                <p className="AdminTextDanger AdminText12 AdminM4R0">Phone number must be exactly 10 digits</p>
              )}
            </div>
          </div>
          <div className="AdminFormGroup">
            <label className="AdminLabel">Pre-text Message</label>
            <textarea
              className="AdminTextarea AdminFullHeight"
              value={wa.pretext || ""}
              onChange={(e) => handleFieldChange("whatsapp.pretext", e.target.value)}
              rows={5}
              placeholder="Hello TheVisaCo!..."
            />
          </div>
        </div>
      </div>
    );
  };

  const renderCalendarEditor = () => {
    const cal = content?.calendar || {};
    const religionColorList = (Array.isArray(cal.religionColorList)
      ? cal.religionColorList
      : Object.entries(cal.religionColors || DEFAULT_RELIGION_COLORS).map(([key, value]) => ({ key, value }))
    ).filter(Boolean);
    const holidays = (cal.holidays || [])
      .map((item, idx) => ({ item, idx }))
      .filter(({ item }) => {
        if (!holidaySearch.trim()) return true;
        const q = holidaySearch.trim().toLowerCase();
        return HOLIDAY_COLUMNS.some((col) => String(item[col.key] || "").toLowerCase().includes(q));
      });
    return (
      <div className="AdminCardMb">
        <div className="AdminSectionHeader">
          <h4 className="AdminSectionTitle">Calendar Holidays</h4>
          <div className="AdminFlexRowGap8 AdminFlexShrink0">
            <div className={`AdminPillSearchWrap ${holidaySearchOpen ? 'is-open' : ''}`}>
              <span className="AdminPillSearchIcon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </span>
              <input
                ref={holidaySearchRef}
                type="text"
                className="AdminPillSearch"
                placeholder="Search..."
                value={holidaySearch}
                onChange={(e) => setHolidaySearch(e.target.value)}
                 onBlur={() => { setHolidaySearch(""); setTimeout(() => setHolidaySearchOpen(false), 200) }}
              />
              {!holidaySearchOpen && (
                <button className="AdminPillSearchOverlay" onClick={() => setHolidaySearchOpen(true)} type="button" aria-label="Search" />
              )}
            </div>
            <button
              className="AdminButton AdminButtonSmall AdminButtonSecondary AdminAddRowBtn"
              onClick={() => handleAddCalendarRowAfter("holidays", (cal.holidays || []).length - 1)}
            >
              + Add Holiday
            </button>
          </div>
        </div>
        <TableWithScrollButtons>
          <table className="AdminTable AdminTableEditor">
            <thead>
              <tr>
                <th className="AdminThOrder">Order</th>
                {HOLIDAY_COLUMNS.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
                <th className="AdminThActions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {holidays.map(({ item, idx }) => (
                  <tr
                    key={idx}
                    draggable="true"
                    onDragStart={(e) => {
                      dragRef.current = { type: "calendar", subField: "holidays", fromIndex: idx };
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("text/plain", JSON.stringify(dragRef.current));
                      e.currentTarget.style.opacity = "0.4";
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                      e.currentTarget.style.outline = "2px dashed #3b82f6";
                      e.currentTarget.style.outlineOffset = "-2px";
                    }}
                    onDragLeave={(e) => { e.currentTarget.style.outline = ""; }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.currentTarget.style.opacity = "";
                      e.currentTarget.style.outline = "";
                      if (!dragRef.current || dragRef.current.type !== "calendar" || dragRef.current.subField !== "holidays") { dragRef.current = null; return; }
                      if (dragRef.current.fromIndex === idx) { dragRef.current = null; return; }
                      setContent((prev) => {
                        const calendar = { ...(prev.calendar || {}) };
                        const arr = [...(calendar.holidays || [])];
                        const [moved] = arr.splice(dragRef.current.fromIndex, 1);
                        arr.splice(idx, 0, moved);
                        calendar.holidays = arr;
                        return { ...prev, calendar };
                      });
                      dragRef.current = null;
                    }}
                    onDragEnd={(e) => {
                      e.currentTarget.style.opacity = "";
                      e.currentTarget.style.outline = "";
                      dragRef.current = null;
                    }}
                  >
                    <td>
                      <div className="AdminOrderBtns">
                        <button className="AdminMoveBtn" onClick={() => handleMoveCalendarRowUp("holidays", idx)} disabled={idx === 0} title="Move up">&#8593;</button>
                        <button className="AdminMoveBtn" onClick={() => handleMoveCalendarRowDown("holidays", idx)} disabled={idx === (cal.holidays || []).length - 1} title="Move down">&#8595;</button>
                      </div>
                    </td>
                    {HOLIDAY_COLUMNS.map((col) => (
                    <td key={col.key}>
                      {col.key === "month" ? (
                        <select
                          className="AdminInput AdminTableInput"
                          value={item.month || 1}
                          onChange={(e) => handleCalendarArrayChange("holidays", idx, "month", parseInt(e.target.value))}
                        >
                          {MONTHS.map((m, i) => (
                            <option key={i + 1} value={i + 1}>{m}</option>
                          ))}
                        </select>
                      ) : col.key === "type" ? (
                        <select
                          className="AdminInput AdminTableInput"
                          value={item.type || "Global"}
                          onChange={(e) => handleCalendarArrayChange("holidays", idx, "type", e.target.value)}
                        >
                          <option value="Global">Global</option>
                          {RELIGION_OPTIONS.map((r) => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                      ) : col.type === "checkbox" ? (
                        <input
                          type="checkbox"
                          className="AdminCheckbox"
                          checked={item.lw === true}
                          onChange={(e) => handleCalendarArrayChange("holidays", idx, "lw", e.target.checked)}
                        />
                      ) : (
                        <input
                          className="AdminInput AdminTableInput"
                          value={item[col.key] || ""}
                          onChange={(e) => handleCalendarArrayChange("holidays", idx, col.key, e.target.value)}
                          placeholder={col.label}
                        />
                      )}
                    </td>
                  ))}
                  <td>
                    <div className="AdminActionBtns">
                      <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => handleAddCalendarRowBefore("holidays", idx)} title="Add row before">&#9650;+</button>
                      <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => handleAddCalendarRowAfter("holidays", idx)} title="Add row after">&#9660;+</button>
                      <button className="AdminIconBtn AdminIconBtnRed" onClick={() => handleDeleteCalendarRow("holidays", idx)} title="Delete row">&#10005;</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWithScrollButtons>
        {holidaySearch.trim() && holidays.length === 0 && (cal.holidays || []).length > 0 && (
          <div className="AdminEmptyState">No holidays found for "{holidaySearch}"</div>
        )}

        <div className="AdminMt24">
          <div className="AdminSectionHeader">
            <h4 className="AdminSectionTitle">Religion Colors</h4>
            <div className="AdminFlexRowGap8 AdminFlexShrink0">
              <div className={`AdminPillSearchWrap ${religionSearchOpen ? 'is-open' : ''}`}>
                <span className="AdminPillSearchIcon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </span>
                <input
                  ref={religionSearchRef}
                  type="text"
                  className="AdminPillSearch"
                  placeholder="Search..."
                  value={religionSearch}
                  onChange={(e) => setReligionSearch(e.target.value)}
                  onBlur={() => { setReligionSearch(""); setTimeout(() => setReligionSearchOpen(false), 200) }}
                />
                {!religionSearchOpen && (
                  <button className="AdminPillSearchOverlay" onClick={() => setReligionSearchOpen(true)} type="button" aria-label="Search" />
                )}
              </div>
              <button
                className="AdminButton AdminButtonSmall AdminButtonSecondary AdminAddRowBtn"
                onClick={() => {
                  const list = religionColorList;
                  handleAddCalendarRowAfter("religionColorList", list.length - 1);
                }}
              >
                + Add Religion
              </button>
            </div>
          </div>
          <TableWithScrollButtons>
            <table className="AdminTable AdminTableEditor">
              <thead>
                <tr>
                  <th className="AdminThOrder">Order</th>
                  <th>Religion</th>
                  <th>Color</th>
                  <th className="AdminThActions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {religionColorList
                  .map((item, idx) => ({ item, idx }))
                  .filter(({ item }) => {
                    if (!religionSearch.trim()) return true;
                    const q = religionSearch.trim().toLowerCase();
                    return (item.key || "").toLowerCase().includes(q) || (item.value || "").toLowerCase().includes(q);
                  })
                  .map(({ item, idx }) => (
                  <tr
                    key={idx}
                    draggable="true"
                    onDragStart={(e) => {
                      dragRef.current = { type: "calendar", subField: "religionColorList", fromIndex: idx };
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("text/plain", JSON.stringify(dragRef.current));
                      e.currentTarget.style.opacity = "0.4";
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                      e.currentTarget.style.outline = "2px dashed #3b82f6";
                      e.currentTarget.style.outlineOffset = "-2px";
                    }}
                    onDragLeave={(e) => { e.currentTarget.style.outline = ""; }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.currentTarget.style.opacity = "";
                      e.currentTarget.style.outline = "";
                      if (!dragRef.current || dragRef.current.type !== "calendar" || dragRef.current.subField !== "religionColorList") { dragRef.current = null; return; }
                      if (dragRef.current.fromIndex === idx) { dragRef.current = null; return; }
                      setContent((prev) => {
                        const calendar = { ...(prev.calendar || {}) };
                        const arr = [...initReligionColorList(calendar)];
                        const [moved] = arr.splice(dragRef.current.fromIndex, 1);
                        arr.splice(idx, 0, moved);
                        calendar.religionColorList = arr;
                        return { ...prev, calendar };
                      });
                      dragRef.current = null;
                    }}
                    onDragEnd={(e) => {
                      e.currentTarget.style.opacity = "";
                      e.currentTarget.style.outline = "";
                      dragRef.current = null;
                    }}
                  >
                    <td>
                      <div className="AdminOrderBtns">
                        <button className="AdminMoveBtn" onClick={() => handleMoveCalendarRowUp("religionColorList", idx)} disabled={idx === 0} title="Move up">&#8593;</button>
                        <button className="AdminMoveBtn" onClick={() => handleMoveCalendarRowDown("religionColorList", idx)} disabled={idx === religionColorList.length - 1} title="Move down">&#8595;</button>
                      </div>
                    </td>
                    <td>
                      <input
                        className="AdminInput AdminTableInput"
                        value={item.key || ""}
                        onChange={(e) => handleCalendarArrayChange("religionColorList", idx, "key", e.target.value)}
                        placeholder="e.g. Hindu"
                      />
                    </td>
                    <td>
                      <div className="AdminFlexRowGap8">
                        <input
                          type="color"
                          value={item.value || "#888888"}
                          onChange={(e) => handleCalendarArrayChange("religionColorList", idx, "value", e.target.value)}
                          className="AdminW36"
                        />
                        <input
                          className="AdminInput AdminTableInput AdminWidth100"
                          value={item.value || ""}
                          onChange={(e) => handleCalendarArrayChange("religionColorList", idx, "value", e.target.value)}
                          placeholder="#hex"
                        />
                      </div>
                    </td>
                    <td>
                      <div className="AdminActionBtns">
                        <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => handleAddCalendarRowBefore("religionColorList", idx)} title="Add row before">&#9650;+</button>
                        <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => handleAddCalendarRowAfter("religionColorList", idx)} title="Add row after">&#9660;+</button>
                        <button className="AdminIconBtn AdminIconBtnRed" onClick={() => handleDeleteCalendarRow("religionColorList", idx)} title="Delete row">&#10005;</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWithScrollButtons>
          {religionColorList.length === 0 && !religionSearch.trim() && (
            <div className="AdminEmptyState">No religions. Click "Add Religion" to create one.</div>
          )}
          {religionSearch.trim() && religionColorList.filter((i) => (i.key||"").toLowerCase().includes(religionSearch.trim().toLowerCase()) || (i.value||"").toLowerCase().includes(religionSearch.trim().toLowerCase())).length === 0 && (
            <div className="AdminEmptyState">No religions found for "{religionSearch}"</div>
          )}
        </div>

        <div className="AdminMt24">
          <div className="AdminSectionHeader">
            <h4 className="AdminSectionTitle">Long Weekend Notes</h4>
            <div className={`AdminPillSearchWrap ${noteSearchOpen ? 'is-open' : ''}`}>
              <span className="AdminPillSearchIcon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </span>
              <input
                ref={noteSearchRef}
                type="text"
                className="AdminPillSearch"
                placeholder="Search..."
                value={noteSearch}
                onChange={(e) => setNoteSearch(e.target.value)}
                onBlur={() => { setNoteSearch(""); setTimeout(() => setNoteSearchOpen(false), 200) }}
              />
              {!noteSearchOpen && (
                <button className="AdminPillSearchOverlay" onClick={() => setNoteSearchOpen(true)} type="button" aria-label="Search" />
              )}
            </div>
            <button
              className="AdminButton AdminButtonSmall AdminButtonSecondary AdminAddRowBtn"
              onClick={() => handleAddCalendarRowAfter("longWeekendNotes", (cal.longWeekendNotes || []).length - 1)}
            >
              + Add Note
            </button>
          </div>
          <TableWithScrollButtons>
            <table className="AdminTable AdminTableEditor">
              <thead>
                <tr>
                  <th className="AdminThOrder">Order</th>
                  {NOTE_COLUMNS.map((col) => (
                    <th key={col.key}>{col.label}</th>
                  ))}
                  <th className="AdminThActions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(cal.longWeekendNotes || [])
                  .map((item, idx) => ({ item, idx }))
                  .filter(({ item }) => {
                    if (!noteSearch.trim()) return true;
                    const q = noteSearch.trim().toLowerCase();
                    return NOTE_COLUMNS.some((col) => String(item[col.key] || "").toLowerCase().includes(q));
                  })
                  .map(({ item, idx }) => (
                  <tr
                    key={idx}
                    draggable="true"
                    onDragStart={(e) => {
                      dragRef.current = { type: "calendar", subField: "longWeekendNotes", fromIndex: idx };
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("text/plain", JSON.stringify(dragRef.current));
                      e.currentTarget.style.opacity = "0.4";
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                      e.currentTarget.style.outline = "2px dashed #3b82f6";
                      e.currentTarget.style.outlineOffset = "-2px";
                    }}
                    onDragLeave={(e) => { e.currentTarget.style.outline = ""; }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.currentTarget.style.opacity = "";
                      e.currentTarget.style.outline = "";
                      if (!dragRef.current || dragRef.current.type !== "calendar" || dragRef.current.subField !== "longWeekendNotes") { dragRef.current = null; return; }
                      if (dragRef.current.fromIndex === idx) { dragRef.current = null; return; }
                      setContent((prev) => {
                        const calendar = { ...(prev.calendar || {}) };
                        const arr = [...(calendar.longWeekendNotes || [])];
                        const [moved] = arr.splice(dragRef.current.fromIndex, 1);
                        arr.splice(idx, 0, moved);
                        calendar.longWeekendNotes = arr;
                        return { ...prev, calendar };
                      });
                      dragRef.current = null;
                    }}
                    onDragEnd={(e) => {
                      e.currentTarget.style.opacity = "";
                      e.currentTarget.style.outline = "";
                      dragRef.current = null;
                    }}
                  >
                    <td>
                      <div className="AdminOrderBtns">
                        <button className="AdminMoveBtn" onClick={() => handleMoveCalendarRowUp("longWeekendNotes", idx)} disabled={idx === 0} title="Move up">&#8593;</button>
                        <button className="AdminMoveBtn" onClick={() => handleMoveCalendarRowDown("longWeekendNotes", idx)} disabled={idx === (cal.longWeekendNotes || []).length - 1} title="Move down">&#8595;</button>
                      </div>
                    </td>
                    {NOTE_COLUMNS.map((col) => (
                      <td key={col.key}>
                        {col.key === "month" ? (
                          <select
                            className="AdminInput AdminTableInput"
                            value={item.month || 1}
                            onChange={(e) => handleCalendarArrayChange("longWeekendNotes", idx, "month", parseInt(e.target.value))}
                          >
                            {MONTHS.map((m, i) => (
                              <option key={i + 1} value={i + 1}>{m}</option>
                            ))}
                          </select>
                        ) : col.key === "days" ? (
                          <input
                            className="AdminInput AdminTableInput"
                            value={typeof item.days === "string" ? item.days : (Array.isArray(item.days) ? item.days.join(",") : "")}
                            onChange={(e) => handleCalendarArrayChange("longWeekendNotes", idx, "days", e.target.value)}
                            placeholder="e.g. 24,25,26"
                          />
                        ) : (
                          <input
                            className="AdminInput AdminTableInput"
                            value={item[col.key] || ""}
                            onChange={(e) => handleCalendarArrayChange("longWeekendNotes", idx, col.key, e.target.value)}
                            placeholder={col.label}
                          />
                        )}
                      </td>
                    ))}
                    <td>
                      <div className="AdminActionBtns">
                        <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => handleAddCalendarRowBefore("longWeekendNotes", idx)} title="Add row before">&#9650;+</button>
                        <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => handleAddCalendarRowAfter("longWeekendNotes", idx)} title="Add row after">&#9660;+</button>
                        <button className="AdminIconBtn AdminIconBtnRed" onClick={() => handleDeleteCalendarRow("longWeekendNotes", idx)} title="Delete row">&#10005;</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWithScrollButtons>
        </div>

        <div className="AdminMt24">
          <div className="AdminSectionHeader">
            <h4 className="AdminSectionTitle">Calendar Settings</h4>
          </div>
          <div className="AdminGrid2Gap16">
            <div className="AdminFormGroup">
              <label className="AdminLabel">Min Year</label>
              <input className="AdminInput" type="number" value={cal.minYear || 2000} onChange={(e) => handleFieldChange("calendar.minYear", parseInt(e.target.value) || 2000)} />
            </div>
            <div className="AdminFormGroup">
              <label className="AdminLabel">Max Year</label>
              <input className="AdminInput" type="number" value={cal.maxYear || 2050} onChange={(e) => handleFieldChange("calendar.maxYear", parseInt(e.target.value) || 2050)} />
            </div>
          </div>
          <div className="AdminFormGroup">
            <label className="AdminLabel">Month Names (comma-separated)</label>
            <input className="AdminInput" value={(cal.monthNames || []).join(", ")} onChange={(e) => handleFieldChange("calendar.monthNames", e.target.value.split(",").map((s) => s.trim()))} />
          </div>
          <div className="AdminFormGroup">
            <label className="AdminLabel">Day of Week (comma-separated)</label>
            <input className="AdminInput" value={(cal.dow || []).join(", ")} onChange={(e) => handleFieldChange("calendar.dow", e.target.value.split(",").map((s) => s.trim()))} />
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="AdminEmptyState">{NAVBAR_MESSAGES.loading}</div>;
  }

  if (!content) {
    return (
      <div>
        <button className="AdminButton AdminButtonSmall AdminButtonSecondary AdminBackBtn" onClick={onBack}>
          &larr; Back to Content Manager
        </button>
        <div className="AdminEmptyState">{NAVBAR_MESSAGES.noContent}</div>
      </div>
    );
  }

  const isDirty = JSON.stringify(content) !== snapshotRef.current;

  return (
    <div className="AdminCard">
      <div className="AdminCardHeader">
        <h3 className="AdminFooterH3">Edit Navbar Content{activeTab !== "data" && Array.isArray(content?.[activeTab]) && <span className="AdminCountBadge">{content[activeTab].length}</span>}</h3>
        <div className="AdminFlexRowGap8 AdminMlAuto">
          <button className="AdminButton AdminButtonSmall AdminSubmitBtn AdminNowrap" onClick={handleSubmit} disabled={!isDirty || saving}>
            {saving ? NAVBAR_MESSAGES.publishing : getSubmitButtonLabel(user?.role)}
          </button>
          <button className="AdminButton AdminButtonSmall AdminSheetBtn" onClick={handleOpenSheet}>Google Sheet</button>
          <button className="AdminSheetIconBtn" onClick={handlePushToSheet} disabled={loading} title="Push to Sheet">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
          </button>
          <button className="AdminSheetIconBtn" onClick={handlePullFromSheet} disabled={loading} title="Pull from Sheet">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </button>
        </div>
      </div>

      {message && (
        <p className={`AdminMsgBox ${message.includes("success") ? "AdminSuccess" : "AdminError"}`}>
          {message}
        </p>
      )}

      <div
        className="AdminTabBar"
        ref={tabBarRef}
        style={{ display: "flex", gap: 0, borderBottom: "2px solid #e2e8f0", marginBottom: 16 }}
      >
        {NAVBAR_TABS.map((tab) => {
          const dupCount = duplicateInfo.filter(d => d.tabId === tab.id).length;
          return (
            <button
              key={tab.id}
              draggable
              onDragStart={(e) => { tabDragRef.current = tab.id; e.target.style.opacity = '0.5'; }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const fromId = tabDragRef.current;
                if (!fromId || fromId === tab.id) return;
                const newOrder = [...NAVBAR_TABS];
                const fromIdx = newOrder.findIndex(t => t.id === fromId);
                const toIdx = newOrder.findIndex(t => t.id === tab.id);
                if (fromIdx === -1 || toIdx === -1) return;
                const [moved] = newOrder.splice(fromIdx, 1);
                newOrder.splice(toIdx, 0, moved);
                setActiveTab(tab.id);
              }}
              onDragEnd={(e) => { e.target.style.opacity = '1'; tabDragRef.current = null; }}
              className={`AdminTabBtn ${activeTab === tab.id ? "AdminTabBtnActive" : ""}`}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: "10px 20px", border: "none", background: activeTab === tab.id ? "#1B2A4A" : "transparent",
                color: activeTab === tab.id ? "#fff" : "#64748b", cursor: "grab", fontSize: 14, fontWeight: 600,
                borderRadius: "8px 8px 0 0", transition: "all 0.2s",
              }}
            >
              {tab.label}
              {dupCount > 0 && (
                <span className="AdminDupBadge" title={`${dupCount} duplicate(s) in ${tab.label}`}>{dupCount}</span>
              )}
            </button>
          );
        })}
      </div>

      {activeTab === "data" && (
        <div className="AdminCard">
          <div className="AdminDataHeader">
            <h4 className="AdminSectionTitle">Data</h4>
          </div>
          <div className="AdminDataInfo">
            <div>
              <span className="AdminDataLabel">Last Updated</span>
              <p className="AdminDataValue">
                {sectionInfo?.updatedAt ? new Date(sectionInfo.updatedAt).toLocaleString() : "Not published"}
              </p>
            </div>
            <div>
              <span className="AdminDataLabel">Section Key</span>
              <p className="AdminDataValue">{section?.key || "navbar"}</p>
            </div>
          </div>
          <textarea
            className="AdminJsonEditor"
            value={JSON.stringify(content, null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                setContent(parsed);
                setMessage("");
              } catch {
                setMessage(NAVBAR_MESSAGES.invalidJson);
              }
            }}
          />
        </div>
      )}

      {activeTab === "toolLinks" && renderArrayEditor("toolLinks", NAVBAR_COLUMNS.toolLinks)}
      {activeTab === "toolImages" && renderToolImagesEditor()}
      {activeTab === "tabs" && renderArrayEditor("tabs", NAVBAR_COLUMNS.tabs)}
      {activeTab === "mobileMenuLinks" && renderArrayEditor("mobileMenuLinks", NAVBAR_COLUMNS.mobileMenuLinks)}
      {activeTab === "mobileQuickActions" && renderArrayEditor("mobileQuickActions", NAVBAR_COLUMNS.mobileQuickActions)}
      {activeTab === "brand" && renderBrandEditor()}
      {activeTab === "whatsapp" && renderWhatsAppEditor()}
      {activeTab === "calendarSettings" && renderCalendarEditor()}

      {logoEditorOpen && (
        <AdminLogoEditor
          currentImage={logoEditorImage}
          logoHistory={logoEditorRefKey ? (content?.imageHistory?.[logoEditorRefKey] || []) : []}
          logoBin={logoEditorRefKey ? (content?.imageBin?.[logoEditorRefKey] || []) : []}
          onSave={handleLogoEditorSave}
          onDeleteHistory={handleLogoEditorDeleteHistory}
          onRestoreFromBin={handleLogoEditorRestoreBin}
          onPermanentDelete={handleLogoEditorPermanentDelete}
          onClose={handleLogoEditorClose}
          accept={logoEditorAccept}
        />
      )}

      {brandLogoEditorOpen && (
        <AdminLogoEditor
          currentImage={content?.brand?.logoImage || ""}
          logoHistory={brandLogoHistory}
          logoBin={brandLogoBin}
          onSave={handleBrandLogoEditorSave}
          onDeleteHistory={handleBrandLogoDeleteHistory}
          onRestoreFromBin={handleBrandLogoRestoreBin}
          onPermanentDelete={(index) => {
            setContent((prev) => {
              const bin = [...(prev?.brand?.logoBin || [])];
              bin.splice(index, 1);
              return { ...prev, brand: { ...prev?.brand, logoBin: bin } };
            });
          }}
          onClose={() => setBrandLogoEditorOpen(false)}
          accept="image/png"
        />
      )}

      {brandGifEditorOpen && (
        <AdminLogoEditor
          currentImage={content?.brand?.logoGif || ""}
          logoHistory={brandGifHistory}
          logoBin={brandGifBin}
          onSave={handleBrandGifEditorSave}
          onDeleteHistory={handleBrandGifDeleteHistory}
          onRestoreFromBin={handleBrandGifRestoreBin}
          onPermanentDelete={(index) => {
            setContent((prev) => {
              const bin = [...(prev?.brand?.gifBin || [])];
              bin.splice(index, 1);
              return { ...prev, brand: { ...prev?.brand, gifBin: bin } };
            });
          }}
          onClose={() => setBrandGifEditorOpen(false)}
          accept="image/gif"
        />
      )}

      {showNoteModal && (
        <div className="AdminModal">
          <div className="AdminModalContent AdminModalMaxW480">
            <div className="AdminFlexBetween AdminMb16">
              <h3 className="AdminModalTitle AdminM0">Describe Your Changes</h3>
              <button className="AdminCloseBtn" onClick={() => setShowNoteModal(false)}>&times;</button>
            </div>
            <p className="AdminText13 AdminTextMuted AdminM0B12">
              Please provide a note describing what changes you made. This is required before submitting.
            </p>
            <textarea
              className="AdminTextarea AdminModelTextarea"
              placeholder="Describe what changed..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              autoFocus
            />
            <div className="AdminFlexCenter AdminMt16">
              <button
                className={`AdminButton AdminSubmitBtn${!noteText.trim() ? " AdminOpacityHalf" : ""}`}
                onClick={handleConfirmSubmit}
                disabled={!noteText.trim() || saving}>
                {saving ? "Submitting..." : "Continue"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessOverlay && (
        <div className="AdminModal" onClick={() => setShowSuccessOverlay(false)}>
          <div className="AdminModalContent AdminModalMaxW400" onClick={e => e.stopPropagation()}>
            <div className="AdminText48">
              {successTitle === "Changes Published" ? "\u2705" : "\uD83D\uDCEB"}
            </div>
            <h3 className="AdminModalTitle AdminMb4">{successTitle}</h3>
            <p className="AdminText13 AdminTextMuted AdminM0B20">
              {successTitle === "Changes Published" ? "Your content changes have been published live." : "Your change request has been submitted for approval."}
            </p>
            <div className="AdminFlexCenter">
              <button
                className="AdminButton AdminSubmitBtn"
                onClick={() => setShowSuccessOverlay(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {showDuplicateModal && duplicateInfo.length > 0 && (
        <div className="AdminModal">
          <div className="AdminModalContent AdminModalMaxW520" style={{position:"relative"}}>
            <button onClick={() => setShowDuplicateModal(false)} style={{ position: "absolute", top: 8, right: 12, background: "none", border: "none", fontSize: 24, cursor: "pointer", color: "#64748b", lineHeight: 1 }} title="Close">&times;</button>
            <div className="AdminText36 AdminTextCenter AdminMb8">&#9888;</div>
            <h3 className="AdminModalTitle AdminTextCenter AdminM0B4">Duplicate Entries Detected</h3>
            <p className="AdminText13 AdminTextMuted AdminTextCenter AdminM0B16">
              The following duplicate entries were found. Choose how to proceed.
            </p>
            <div className="AdminDupWarningBox">
              {(() => {
                const byTab = {};
                for (const d of duplicateInfo) {
                  if (!byTab[d.tabId]) byTab[d.tabId] = { label: d.tabLabel, items: [] };
                  byTab[d.tabId].items.push(d.duplicateValue);
                }
                return Object.entries(byTab).map(([tabId, info]) => (
                  <div key={tabId} className="AdminDupItem">
                    <strong className="AdminTextDark">{info.label}</strong>
                    <span className="AdminTextSlate"> &mdash; Duplicate{info.items.length > 1 ? "s" : ""}: </span>
                    <span className="AdminTextDark">{info.items.join(", ")}</span>
                  </div>
                ));
              })()}
            </div>
            <div className="AdminFlexCenterG12">
              <button
                className="AdminButton AdminSubmitBtn AdminBtnDanger"
                onClick={handleRemoveDuplicates}
                disabled={duplicateActionLoading}
              >
                {duplicateActionLoading ? "Removing..." : "Remove Duplicate Rows"}
              </button>
              <button
                className="AdminButton AdminSubmitBtn"
                onClick={handleContinueAnyway}
                disabled={duplicateActionLoading}
              >
                Continue Anyway
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default AdminNavbarEditor;
